<!-- components/MarkdownTabs.vue -->
<template>
  <div class="markdown-tabs">
    <div
      ref="markdownContainer"
      v-html="processedContent"
    />
  </div>
</template>
  
  <script>
  import { marked } from 'marked';
  import hljs from 'highlight.js';
  import 'highlight.js/styles/atom-one-dark.css';
  
  export default {
    name: 'MarkdownTabs',
    props: {
      content: {
        type: String,
        required: true
      }
    },
    data() {
      return {
        processedContent: ''
      };
    },
    mounted() {
      // Initialize marked with syntax highlighting
      marked.setOptions({
        highlight: function(code, lang) {
          if (lang && hljs.getLanguage(lang)) {
            return hljs.highlight(code, { language: lang }).value;
          }
          return hljs.highlightAuto(code).value;
        },
        langPrefix: 'hljs language-'
      });
      
      // Process the content to replace onclick attributes
      const processedContent = this.content.replace(/onclick="openTab\(event, '([^']+)'\)"/g, 
        'data-tab="$1"');
      
      // Render the processed markdown content
      this.processedContent = marked(processedContent);
      
      // Initialize the tabs functionality after Vue has updated the DOM
      this.$nextTick(() => {
        this.initTabs();
        this.setupCodeCopy();
      });
    },
    methods: {
      initTabs() {
        // Get all tab buttons
        const tabButtons = this.$refs.markdownContainer.querySelectorAll('.tab-btn');
        
        // Add click event listeners to each tab button
        tabButtons.forEach(button => {
          button.addEventListener('click', () => {
            const tabId = button.getAttribute('data-tab');
            if (tabId) {
              this.openTab(button, tabId);
            }
          });
        });
        
        // Activate the first tab
        if (tabButtons.length > 0) {
          const firstTabId = tabButtons[0].getAttribute('data-tab');
          if (firstTabId) {
            this.openTab(tabButtons[0], firstTabId);
          }
        }
      },
      openTab(button, tabName) {
        // Hide all tab content
        const tabContents = this.$refs.markdownContainer.querySelectorAll('.tab-content');
        tabContents.forEach(content => {
          content.classList.remove('active');
        });
        
        // Remove active class from all tab buttons
        const tabButtons = this.$refs.markdownContainer.querySelectorAll('.tab-btn');
        tabButtons.forEach(btn => {
          btn.classList.remove('active');
        });
        
        // Show the current tab and add active class to the button
        const tab = this.$refs.markdownContainer.querySelector(`#${tabName}`);
        if (tab) {
          tab.classList.add('active');
        }
        
        button.classList.add('active');
        
        // Re-setup copy buttons for the newly visible tab
        this.$nextTick(() => {
          this.setupCodeCopy();
        });
      },
      setupCodeCopy() {
        // Find all code blocks
        const codeBlocks = this.$refs.markdownContainer.querySelectorAll('pre code');
        
        codeBlocks.forEach((codeBlock, index) => {
          const parentPre = codeBlock.parentElement;
          
          // Check if copy button already exists
          if (!parentPre.querySelector('.copy-button')) {
            // Create copy button
            const copyButton = document.createElement('button');
            copyButton.className = 'copy-button';
            copyButton.textContent = 'Copy';
            copyButton.setAttribute('aria-label', 'Copy code to clipboard');
            copyButton.setAttribute('data-index', index);
            
            // Add click handler
            copyButton.addEventListener('click', (e) => {
              e.stopPropagation(); // Prevent event bubbling
              
              // Get the code text
              const code = codeBlock.innerText;
              
              // Copy to clipboard
              navigator.clipboard.writeText(code)
                .then(() => {
                  // Provide visual feedback
                  copyButton.textContent = 'Copied!';
                  copyButton.classList.add('copied');
                  
                  // Reset button after delay
                  setTimeout(() => {
                    copyButton.textContent = 'Copy';
                    copyButton.classList.remove('copied');
                  }, 2000);
                })
                .catch(err => {
                  console.error('Failed to copy text: ', err);
                  copyButton.textContent = 'Failed';
                  
                  // Reset button after delay
                  setTimeout(() => {
                    copyButton.textContent = 'Copy';
                  }, 2000);
                });
            });
            
            // Add button to the pre element
            parentPre.appendChild(copyButton);
            
            // Add position relative to parent if not already set
            if (window.getComputedStyle(parentPre).position !== 'relative') {
              parentPre.style.position = 'relative';
            }
          }
        });
      }
    }
  };
  </script>
  
  <style scoped>
  .markdown-tabs {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
    color: #333;
    line-height: 1.6;
  }
  
  :deep(.tab-container) {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    overflow: hidden;
    background-color: white;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
    max-width: 2400px;
    margin: 4rem auto;
  }
  
  :deep(.tab-content) {
    display: none;
    padding: 5rem;
    background-color: white;
    min-height: 1000px;
  }
  
  :deep(.tab-nav) {
    display: flex;
    overflow-x: auto;
    white-space: nowrap;
    padding: 0;
    border-bottom: 1px solid #e5e7eb;
    background-color: white;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */
  }
  
  :deep(.tab-nav::-webkit-scrollbar) {
    display: none; /* Chrome, Safari, Opera */
  }
  
  :deep(.tab-btn) {
    flex: 0 0 auto;
    border: none;
    background-color: transparent;
    padding: 0.75rem 1.25rem;
    cursor: pointer;
    font-weight: 500;
    color: #6b7280;
    border-bottom: 2px solid transparent;
    transition: all 0.2s;
    position: relative;
  }
  
  :deep(.tab-btn:hover) {
    color: #1A56DB; /* Blue color from your site */
  }
  
  :deep(.tab-btn.active) {
    color: #1A56DB; /* Blue color from your site */
    border-bottom-color: #1A56DB; /* Blue color from your site */
    background-color: white;
  }
  
  :deep(.tab-btn.active::after) {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: #1A56DB; /* Blue color from your site */
  }
  
  :deep(.tab-content) {
    display: none;
    padding: 3rem;
    background-color: white;
    min-height: 600px;
  }
  
  :deep(.tab-content.active) {
    display: block;
  }
  
  :deep(h3) {
    font-size: 1.25rem;
    font-weight: 600;
    margin-top: 1.5rem;
    margin-bottom: 1rem;
    color: #1f2937;
  }
  
  :deep(pre) {
    background-color: #EBF5FF; /* Dark background for code blocks */
    color: #1F2937;
    padding: 1rem;
    border-radius: 0.375rem;
    overflow-x: auto;
    margin: 1rem 0;
    position: relative;
  }
  
  :deep(code) {
    font-family: 'Inter', sans-serif;
    font-size: 0.875rem;
  }
  
  :deep(p) {
    margin-bottom: 1rem;
  }
  
  :deep(ul) {
    list-style-type: disc;
    padding-left: 1.5rem;
    margin-bottom: 1rem;
  }
  
  :deep(li) {
    margin-bottom: 0.5rem;
  }
  
  :deep(a) {
    color: #1A56DB; /* Blue color from your site */
    text-decoration: none;
  }
  
  :deep(a:hover) {
    text-decoration: underline;
  }
  
  /* Add copy button styles */
  :deep(.copy-button) {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    padding: 0.25rem 0.5rem;
    background-color: #1A56DB;
    color: #ffffff;
    border: none;
    border-radius: 0.25rem;
    font-size: 0.75rem;
    cursor: pointer;
    transition: all 0.2s;
  }
  
  :deep(.copy-button:hover) {
    background-color: rgba(255, 255, 255, 0.2);
  }
  
  :deep(.copy-button.copied) {
    background-color: #859cce; /* Green color for success from your site */
    color: white;
  }
  
  /* Media query for small screens - keep tabs horizontal */
  @media (max-width: 640px) {
    :deep(.tab-nav) {
      padding: 0;
    }
    
    :deep(.tab-btn) {
      padding: 0.75rem 1rem;
      font-size: 0.875rem;
    }
  }
  </style>